import { API_CENTRE } from "./config";
import _o2sService from "./_o2sServices";
import { IFilter } from "../models/filters/filter";
import { IStockFilter } from "../models/filters/stockFilter";
import { IStockForm } from "../models/forms/stockForm";
import { IStockPDTO } from "../models/dtos/stockPDTO";
import { apiCallWithAuth } from "../helper/apiManager";
import { resolveAxiosErrorMessage } from "../helper/apiError";
import type { IPendingReceptionSummary, IPendingReceptionResult } from "../models/pendingReception";

export default class StockService extends _o2sService {

    protected static override mainUrl:string = API_CENTRE + "/api/v1/Stock";

    protected static override  getUrlFilter<F extends IStockFilter>(filter?: F):string {
        let url = super.getUrlFilter(filter as IFilter);
        if ( filter != null)
        {
            if(filter.codeStockId != undefined)
                url += `&CodeStockId=${filter.codeStockId}`;
            if(filter.articleId != undefined)
                url += `&ArticleId=${filter.articleId}`;
            if(filter.centerId != undefined)
                url += `&CenterId=${filter.centerId}`;
            if(filter.unityId != undefined)
                url += `&UnityId=${filter.unityId}`;
            // US-13 (E13-F02-U13): packaging-identity filters
            if(filter.codeStockCode != undefined)
                url += `&CodeStockCode=${encodeURIComponent(filter.codeStockCode)}`;
            if(filter.articleCode != undefined)
                url += `&ArticleCode=${encodeURIComponent(filter.articleCode)}`;
            if(filter.conditionnementCode != undefined)
                url += `&ConditionnementCode=${encodeURIComponent(filter.conditionnementCode)}`;
            if(filter.volumeDose != undefined)
                url += `&VolumeDose=${filter.volumeDose}`;
            if(filter.spzDose != undefined)
                url += `&SpzDose=${filter.spzDose}`;
            if(filter.isArmada != undefined)
                url += `&IsArmada=${filter.isArmada}`;
            if(filter.heterospermie != undefined)
                url += `&Heterospermie=${filter.heterospermie}`;
        }

        return url;
    }

    public static async resetStock(centerCodeSpecies:string)
        {
            try {
                let result = await apiCallWithAuth(this.mainUrl + `/reset?centerBusinessKey=${centerCodeSpecies}`);
                return this.ReturnObject(result);
            } catch (err) {
                return this.ReturnError(err);
            }
        }

    /**
     * US-13 (E13-F02-U13): packaging-end adjustment.
     * Creates an FI+ or FI- movement depending on the delta sign and updates
     * the dispatch's packaging-end value (numberDosesManufactured).
     */
    public static async adjustPackagingEnd(payload: {
        dispatchManufactoringId: number;
        newQuantity: number;
        agentId: number;
        stockMovement: Record<string, any>;
    }) {
        try {
            const result = await apiCallWithAuth(this.mainUrl + "/adjust-packaging-end", "post", payload);
            return this.ReturnObject(result);
        } catch (err: any) {
            // ReturnError ne garde que le message axios : on remonte le motif métier du refus
            // (0028509 : correction supérieure à la fin de conditionnement), comme destroyBatch.
            const result: any = this.ReturnError(err);
            result.businessCode = err?.response?.data?.code ?? null;
            result.businessMessage = resolveAxiosErrorMessage(err);
            return result;
        }
    }

    /**
     * US-13 (E13-F02-U13): stock reinsertion.
     * Creates a REI movement; does not alter the packaging-end value.
     */
    public static async reinsertStock(payload: {
        dispatchManufactoringId: number;
        newQuantity: number;
        agentId: number;
        motifAjustementId: number;
        stockMovement: Record<string, any>;
    }) {
        try {
            const result = await apiCallWithAuth(this.mainUrl + "/reinsert", "post", payload);
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    public static async getConsultation(centerId?: number, codeStockId?: number) {
        try {
            let url = this.mainUrl + "/consultation?";
            if (centerId != undefined) url += `centerId=${centerId}&`;
            if (codeStockId != undefined) url += `codeStockId=${codeStockId}&`;
            let result = await apiCallWithAuth(url);
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    public static async receiveBatch(destinationCenterId: number, items: { stockMovementId: number; action: number }[], commentaire?: string, agentId?: number) {
        try {
            const result = await apiCallWithAuth(
                this.mainUrl + `/receive?destinationCenterId=${destinationCenterId}`,
                "post",
                { items, commentaire, agentId }
            );
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    public static async getPendingReceptionsSummary(destinationCenterId: number) {
        try {
            const result = await apiCallWithAuth(this.mainUrl + `/pending-receptions/summary?destinationCenterId=${destinationCenterId}`);
            return this.ReturnAttribut<IPendingReceptionSummary[]>(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    public static async getPendingReceptions(destinationCenterId: number) {
        try {
            const result = await apiCallWithAuth(this.mainUrl + `/pending-receptions?destinationCenterId=${destinationCenterId}`);
            return this.ReturnAttribut<IPendingReceptionResult[]>(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    public static override toDTO(obj:IStockForm):IStockPDTO {
        const dto = super.toDTO(obj) as IStockPDTO;
        return dto;
    }
}